import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Logo from '../components/Logo';
import { loginHref, slackInstallHref } from '../utils/slack';
import {
  bodyFont,
  ink,
  inkMuted,
  pop,
  popDeep,
  white,
} from '../theme/palette';

// Shared by the marketing bundle (PublicApp) and the product bundle (App), which
// previously kept two drifting copies of this markup. The only real difference
// was analytics, so that arrives as a prop.
export interface SiteChromeProps {
  onCtaClick?: () => void;
  /**
   * The product bundle deliberately carries no blog routes — a hundred posts
   * have no business in it. Inside that app the Blog link has to be a real
   * navigation so Express serves the prerendered page instead of the router
   * resolving it to a 404.
   */
  blogHardLink?: boolean;
}

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 40px;
  background: ${white};

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  height: 100%;
  max-width: 1160px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  /* Blog makes five links before the pill. 22px is the most that still clears
     the wordmark at 1024px; the 900px rule below culls them anyway. */
  gap: 22px;
  line-height: 1;

  @media (max-width: 900px) {
    gap: 16px;
  }
`;

const navigationLinkStyles = css<{ $active?: boolean }>`
  font: 600 15px/1 ${bodyFont};
  color: ${props => (props.$active ? pop : ink)};
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: ${pop};
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavigationAnchor = styled.a<{ $active?: boolean }>`${navigationLinkStyles}`;
const NavigationLink = styled(Link)<{ $active?: boolean }>`${navigationLinkStyles}`;

/**
 * Pricing survives the 900px cull that hides the rest of the marketing links.
 * Once "Start for free" drops out on phones it is the only route left to the
 * plans, so it earns the space — until 360px, where the lockup and Login have
 * the bar to themselves.
 */
const PricingLink = styled(NavigationLink)`
  @media (max-width: 900px) {
    display: inline-flex;
  }

  @media (max-width: 360px) {
    display: none;
  }
`;

const StartFreeLink = styled.a`
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  padding: 0 22px;
  border-radius: 999px;
  background: ${pop};
  color: ${white};
  font: 700 15px/1 ${bodyFont};
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: ${popDeep};
    color: ${white};
    transform: translateY(-1px);
  }

  /* The hero's own CTA sits a short scroll below, so on phones this pill is
     redundant weight crowding the wordmark. */
  @media (max-width: 560px) {
    display: none;
  }
`;

const LoginLink = styled.a`
  font: 600 15px/1 ${bodyFont};
  color: ${inkMuted};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: ${ink};
  }
`;

export default function SiteHeader({
  onCtaClick,
  blogHardLink = false,
}: SiteChromeProps) {
  const { pathname } = useLocation();
  const onBlog = pathname === '/blog' || pathname.startsWith('/blog/');

  return (
    <Header>
      <HeaderInner>
        <LogoLink to="/" aria-label="Poplearn home">
          <Logo height={48} />
        </LogoLink>
        <Navigation aria-label="Primary navigation">
          <NavigationAnchor href="/#the-experience">Product</NavigationAnchor>
          <NavigationAnchor href="/#how-it-works">How it works</NavigationAnchor>
          <PricingLink to="/pricing">Pricing</PricingLink>
          <NavigationLink to="/support">Support</NavigationLink>
          {blogHardLink ? (
            <NavigationAnchor href="/blog" $active={onBlog}>
              Blog
            </NavigationAnchor>
          ) : (
            <NavigationLink
              to="/blog"
              $active={onBlog}
              aria-current={onBlog ? 'page' : undefined}
            >
              Blog
            </NavigationLink>
          )}
          <StartFreeLink href={slackInstallHref} onClick={onCtaClick}>
            Start for free
          </StartFreeLink>
          <LoginLink href={loginHref}>Login</LoginLink>
        </Navigation>
      </HeaderInner>
    </Header>
  );
}
